import { styled } from '@mui/material/styles';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ItemContent, Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { IConversationMessage } from 'src/@types/conversation';
import { useConversationContext } from 'src/provider/ConversationContext';
import { ConversationGoToBottom } from './ConversationGoToBottom';

const List = styled('div')(({ theme }) => ({
  '& > *:last-child > *': {
    paddingBottom: `${theme.spacing(8)}`,
  },
}));

type Props<R extends IConversationMessage> = {
  messages: R[];
  goToMessage: string | undefined;
  setGoToMessage(messageId: string | undefined): void;
  scrollEnd: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  hasNextPage: boolean | undefined;
  hasPreviousPage: boolean | undefined;
  fetchNextPage(): Promise<any>;
  fetchPreviousPage(): Promise<any>;
  itemContent: ItemContent<any, any>;
};

export const ConversationVirtuoso = <R extends IConversationMessage>({
  messages,
  goToMessage,
  setGoToMessage,
  scrollEnd,
  isFetching,
  isFetchingNextPage,
  isFetchingPreviousPage,
  hasNextPage,
  hasPreviousPage,
  fetchNextPage,
  fetchPreviousPage,
  itemContent,
}: Props<R>) => {
  const parentRef = useRef<VirtuosoHandle | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const firstItem = useRef<string | undefined>();
  const lastItem = useRef<string | undefined>();
  const dontGoTo = useRef<boolean>(true);
  const initial = useRef<{ up: boolean; down: boolean }>({
    up: true,
    down: true,
  });

  const { setLoadingMore } = useConversationContext();

  const findMessageIndex = (messageId = goToMessage) =>
    messages.findIndex((message) => message._id === messageId);

  const isFetchingMessages =
    isFetching || isFetchingNextPage || isFetchingPreviousPage;

  useEffect(() => {
    dontGoTo.current = !goToMessage;
  }, [goToMessage]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useLayoutEffect(() => {
    if (!parentRef || !goToMessage || !isFetchingMessages || dontGoTo.current)
      return;
    initial.current = { up: true, down: true };
    const firstItemIndex = findMessageIndex(goToMessage);
    parentRef.current?.scrollToIndex({
      index: firstItemIndex,
      align: 'center',
    });
    initial.current = { up: false, down: false };
  }, [goToMessage, isFetchingMessages, parentRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useLayoutEffect(() => {
    if (
      !messages.length ||
      (!firstItem.current && !lastItem.current && !goToMessage) ||
      isFetchingMessages
    )
      return;

    const scrollItem = goToMessage || firstItem.current || lastItem.current;
    const itemIndex = findMessageIndex(scrollItem);
    parentRef.current?.scrollToIndex({
      index: itemIndex,
      align: goToMessage ? 'center' : firstItem.current ? 'start' : 'end',
    });
    firstItem.current = undefined;
    lastItem.current = undefined;
  }, [messages, isFetchingMessages, goToMessage]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useLayoutEffect(() => {
    if (!scrollEnd) return;
    setGoToMessage(undefined);
    parentRef.current?.scrollToIndex({
      index: 'LAST',
      align: 'end',
    });
  }, [scrollEnd]);

  const handleUp = async (e: boolean) => {
    if (initial.current.up) {
      initial.current.up = false;
      return;
    }
    if (!e || !hasNextPage) return;
    dontGoTo.current = true;

    setLoadingMore('up');
    await fetchNextPage();
    setLoadingMore(false);
    firstItem.current = messages[0]._id;
  };

  const handleDown = async (e: boolean) => {
    if (!e || !hasPreviousPage) return;
    dontGoTo.current = true;
    if (initial.current.down) {
      initial.current.down = false;
      return;
    }
    setLoadingMore('down');
    await fetchPreviousPage();
    setLoadingMore(false);
    lastItem.current = messages[messages.length - 1]._id;
  };

  return (
    <>
      <Virtuoso
        ref={parentRef}
        itemContent={itemContent}
        initialTopMostItemIndex={messages.length - 1}
        data={messages}
        followOutput="auto"
        increaseViewportBy={600}
        computeItemKey={(index) => messages[index]._id}
        atTopStateChange={handleUp}
        atBottomStateChange={handleDown}
        components={{ List }}
        onScroll={(e) => {
          const currentPosition = (e.target as any).scrollTop;
          const viewportHeight = (e.target as any).clientHeight;
          const scrollHeight = (e.target as any).scrollHeight;
          const isAtBottom =
            currentPosition + viewportHeight >= scrollHeight - 200;

          setIsAtBottom(isAtBottom);
        }}
      />
      <ConversationGoToBottom
        isAtBottom={isAtBottom}
        onClick={() => {
          parentRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
          setGoToMessage(undefined);
        }}
      />
    </>
  );
};
